import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import Button from "./Button";
import {
  MODERNIZED_MODAL_HEADING_ID,
  MODERNIZED_MODAL_DISMISS_ANIMATION_TIME,
  MODERNIZED_MODAL_ANIMATION_TIME,
  MODERNIZED_MODAL_SMALL_SCREEN
} from "../../utils/constants";
import { useScreenWidth } from "../../hooks/useScreenWidth";

function ModernizedDialog({
  appData,
  components: {
    ModernizedModalOverlay: Overlay,
    ModernizedModalBody: Body,
    ModernizedModalHeaderContainer: HeaderContainer,
    ModernizedModalHeader: Header,
    ModernizedModalContent: Content,
    ModernizedModalFooter: Footer,
    ModernizedDismissibleButton: CloseButton
  },
  active,
  title,
  label,
  actions = [],
  dialogOverlay = true,
  formRef,
  children,
  headerClass = "",
  dialogClass = "",
  footerClass = "",
  meta = {},
  onClose,
  dismissTime = null,
  withClose = false,
  i18nDismissLabel
}) {
  const [dialogTitle, addDialogTitle] = useState("");
  const [hidden, setHidden] = useState(true);
  const dialogAriaProps = dialogTitle
    ? {
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": MODERNIZED_MODAL_HEADING_ID
      }
    : {};
  const animationTime = dismissTime
    ? MODERNIZED_MODAL_DISMISS_ANIMATION_TIME
    : MODERNIZED_MODAL_ANIMATION_TIME;
  const screenWidth = useScreenWidth();

  // Change buttons order in the DOM so that we could have proper focus order
  const actionsToRender =
    screenWidth >= MODERNIZED_MODAL_SMALL_SCREEN
      ? [...actions].sort((a, b) => (a.variant < b.variant ? 1 : -1))
      : actions;

  const closeModal = () => {
    setHidden(true);
    setTimeout(() => {
      onClose();
    }, animationTime);
  };

  const handleClickOutside = () => {
    if (dismissTime) {
      return;
    }
    closeModal();
  };

  useEffect(() => {
    if (active) {
      // Rewrite onClick for close button to add animation
      const closeBtn = actionsToRender.find(btn => btn.variant === "tertiary");
      if (closeBtn) {
        closeBtn.onClick = closeModal;
      }
      appData.initializeDialog();
      setTimeout(() => {
        setHidden(false);
      });
    }
    return () => {
      setHidden(true);
    };
  }, [active]);

  useEffect(() => {
    addDialogTitle(title);
  }, [title]);

  useEffect(() => {
    if (dismissTime) {
      const timeDismissId = setTimeout(() => {
        setHidden(true);
      }, dismissTime);
      const timeCloseId = setTimeout(() => {
        onClose();
      }, dismissTime + animationTime);
      return () => {
        clearTimeout(timeDismissId);
        clearTimeout(timeCloseId);
      };
    }
  }, [active]);

  const dialogBody = active ? (
    <Overlay
      meta={meta}
      dialogOverlay={dialogOverlay}
      overlayClass={hidden ? "hidden" : "visible"}
    >
      <Body
        bodyClass={`${dialogClass} ${hidden ? " hidden" : " visible"}`}
        handleClickOutside={handleClickOutside}
        {...dialogAriaProps}
      >
        {withClose && (
          <CloseButton label={i18nDismissLabel} onClick={closeModal} />
        )}

        {dialogTitle && (
          <HeaderContainer headerClass={headerClass}>
            <Header id={MODERNIZED_MODAL_HEADING_ID} headerClass={headerClass}>
              {dialogTitle}
            </Header>
          </HeaderContainer>
        )}

        {children}

        {label && <Content>{label}</Content>}

        {!!actionsToRender.length && (
          <Footer footerClass={footerClass}>
            {actionsToRender.map(
              (
                {
                  name,
                  label: visibleLabel,
                  variant,
                  updatedRole,
                  i18nLabel,
                  onClick
                },
                index
              ) => {
                const ariaProps = { "aria-label": i18nLabel };
                if (updatedRole) {
                  ariaProps.role = updatedRole;
                }
                return (
                  <Button
                    name={name || `modal-action-${index}`}
                    key={visibleLabel}
                    size='md'
                    variant={variant}
                    onClick={onClick}
                    {...ariaProps}
                  >
                    <span aria-hidden='true'>{visibleLabel}</span>
                  </Button>
                );
              }
            )}
          </Footer>
        )}
      </Body>
    </Overlay>
  ) : null;

  const body = formRef?.current || document.getElementsByTagName("body")[0];
  return ReactDOM.createPortal(dialogBody, body);
}

ModernizedDialog.propTypes = {
  active: PropTypes.bool.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func
    })
  ),
  title: PropTypes.string,
  label: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  dismissTime: PropTypes.number,
  withClose: PropTypes.bool,
  components: PropTypes.shape({
    ModernizedDismissibleButton: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    ModernizedModalOverlay: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    ModernizedModalBody: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    ModernizedModalHeaderContainer: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    ModernizedModalHeader: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    ModernizedModalContent: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    ModernizedModalFooter: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func
    ]),
    Button: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
  }).isRequired,
  i18nDismissLabel: PropTypes.string
};

export default ModernizedDialog;
